import _ from 'underscore';
import FlowUtil from './FlowUtil';
import defaultJumpToCheck from './modelhelpers/JumpToStepCheck';
import snapshotBuilder from './modelhelpers/SnapshotBuilder';
/**
 * A factory function that returns an object for interacting with a wizard flow
 *
 * @param {Object} flow                  - The definition of the entry, exit and steps of a flow
 * @param {Object} Q                     - ES6 Promise API implementation
 * @param {Function} publishWizardEvent  - Function to generate wizard events
 * @param {Function} stepChangeFns  - Function to get called on flow step transitions to trigger router transition
 * @param {Object} modalBehavior         - Provides promises resulting from displaying information dialogs
 *                     actionStatusInfo - (modals for displaying progress & error messages of calls to the server)
 *                     snapshotResetConfirmation - (confirmation message to prompt that data may be lost when reset to snapshot)
 *                     cancelConfirmation - (confirmation message to cancel flow)
 *
 * @returns {Object} Wizard Model
 **/
export default function (flow, Q, publishWizardEvent, stepChangeFns, modalBehavior) {
    var currentSnapshot = void 0;
    var _jumpToStepCheck = defaultJumpToCheck;
    var stepHistory = FlowUtil.isStepObject(flow.entry) ? [flow.entry] : [];
    var stepChangeListeners = stepChangeFns.concat();
    var transitionStartListeners = [];
    var transitionFinishListeners = [];

    /*
     * Function that takes in a step and executes the transition in response to an event.
     * If the result of that transition is an Junction then it will keep calling the next transition
     * until a step or flow exit is reached. At that point the step change listeners and route change handler will be
     * called.
     */
    function goToStepForEvent(flowModel, eventName, _model, startStep) {
        var _extraArgs = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};

        function executeBeforeTransitionListeners(actionHandlerObject) {
            transitionStartListeners.forEach(function (listenerFn) {
                return listenerFn(actionHandlerObject);
            });
        }

        function executeAfterTransitionListeners(resolution) {
            transitionFinishListeners.forEach(function (listenerFn) {
                return listenerFn(resolution);
            });

            return resolution; // Pass resolution forward
        }

        /*
         * Return the promise of a single transition to a step or junction
         */
        function getTransitionPromise(model, step, args) {
            var actionHandlerObject = step[eventName].doAction();
            executeBeforeTransitionListeners(actionHandlerObject);

            if (actionHandlerObject) {
                // promise that resolves to an object - {model, extraArgs}
                var actionPromise = actionHandlerObject.action(model, args);
                var actionStatusHandler = modalBehavior.actionStatusInfo;
                // return promise that resolves to an object - {model, step, extraArgs}
                return actionStatusHandler(actionPromise.then(executeAfterTransitionListeners), actionHandlerObject.msgs);
            }

            // if just a branch or goTo - convert sync into promise that is immediately resolved
            var nextStepOrJct = FlowUtil.getNextSyncStepOrJunction(flow, step, model, args);
            // promise resolving to model and next step
            return Q.resolve({ model: model, step: nextStepOrJct, extraArgs: args }).then(executeAfterTransitionListeners);
        }

        /*
         * If the result of a next transition is a junction instead of a step or flow exit then we recursively
         * go to the next transition until a step is reached.
         */
        function finalTransitionPromise(stepTransitionPromise) {
            function successAndErrorCb(_ref) {
                var model = _ref.model,
                    step = _ref.step,
                    extraArgs = _ref.extraArgs;

                step = FlowUtil.getStepOrJunctionObject(step, flow);
                if (step._type === 'STEP' || flow.exits[step.name]) {
                    return {
                        model: model,
                        step: step,
                        extraArgs: extraArgs
                    };
                }
                var transitionPromise = getTransitionPromise(model, step, extraArgs);
                return finalTransitionPromise(transitionPromise);
            }

            return stepTransitionPromise.then(successAndErrorCb, successAndErrorCb);
        }

        /*
         * If the final reached point in the flow is a Step then set the current step and fire stepChange and route listeners
         * If the final point reached in the flow is a Junction and an exit point then fire the route listener
         */
        function fireListenersForFinalTransition(_ref2) {
            var model = _ref2.model,
                step = _ref2.step,
                extraArgs = _ref2.extraArgs;

            var nextStep = FlowUtil.getStepOrJunctionObject(step, flow);
            flow.currentStep = nextStep;
            if (FlowUtil.isStepObject(nextStep, flow)) {
                var previouslySeenIndex = getStepIndexStepArray(flow.currentStep.name, stepHistory);
                if (previouslySeenIndex !== -1) {
                    // set the history back to where the step was first encountered
                    stepHistory = stepHistory.slice(0, previouslySeenIndex + 1);
                } else {
                    stepHistory.push(flow.currentStep);
                }
                executeStepChangeListeners(model, startStep, nextStep, extraArgs);
            } else if (flow.exits[nextStep.name]) {
                // junction as flow exit
                executeStepChangeListeners(model, startStep, nextStep, extraArgs);
            }
        }

        flowModel.snapshot = _model;
        var finalPromise = finalTransitionPromise(getTransitionPromise(_model, startStep, _extraArgs));
        return finalPromise.then(function (result) {
            // if the transition succeeded then set the snapshot with the model that may have been updated following actions
            flowModel.snapshot = result.model;

            fireListenersForFinalTransition(result);
            return result;
        }).catch(function (error) {
            fireListenersForFinalTransition(error);
            throw error;
        });
    }

    /**
     * @param {String} stepName
     * @param {Object} steps
     * @returns {number} the index of a step from an array of step based on step name
     */
    function getStepIndexStepArray(stepName, steps) {
        var previousStepNames = _.pluck(steps, 'name');
        return previousStepNames.indexOf(stepName);
    }

    /*
     * Call each of the stepchange listeners with the model
     */
    function executeStepChangeListeners(model, fromStep, toStep, extraArgs) {
        var listeners = stepChangeListeners.concat();
        listeners.forEach(function (listenerFn) {
            return listenerFn(model, fromStep, toStep, extraArgs);
        });
    }

    function _removeFromArray(arr, item) {
        var removeIndex = arr.indexOf(item);
        if (removeIndex !== -1) {
            arr.splice(removeIndex, 1);
        }
    }

    return {
        /**
         * Register callback function to be called when next and previous transitions are initiated
         * Callback function is passed flow, step and model parameters
         *
         * @param {Function} callback
         */
        addTransitionStartListener: function addTransitionStartListener(callback) {
            transitionStartListeners.push(callback);
        },
        addTransitionFinishListener: function addTransitionFinishListener(callback) {
            transitionFinishListeners.push(callback);
        },
        removeTransitionStartListener: function removeTransitionStartListener(callback) {
            _removeFromArray(transitionStartListeners, callback);
        },
        removeTransitionFinishListener: function removeTransitionFinishListener(callback) {
            _removeFromArray(transitionFinishListeners, callback);
        },


        /**
         * Register callback function to be called on next and previous transitions
         * Callback function is passed flow, step and model parameters
         *
         * @param {Function} callback
         */
        addStepChangeListener: function addStepChangeListener(callback) {
            stepChangeListeners.push(callback);
        },


        /**
         * Remove a listener.
         *
         * @param {Function} callback
         * @throws {Error} if there is no matching listener function
         */
        removeStepChangeListener: function removeStepChangeListener(callback) {
            _removeFromArray(stepChangeListeners, callback);
        },


        /**
         * Remove all listeners.
         */
        removeAllStepChangeListeners: function removeAllStepChangeListeners() {
            stepChangeListeners.length = 0;
        },


        /**
         * Provides an alternative implementation to the default function that determines if a user can directly
         * transition to another step.
         * The default implementation only allows a user to transition to a previous step as long as there are no
         * gate points between the current step and the destination see @ modelhelpers/JumpToStepCheck
         *
         * @param {Function} jumpCheckImplementation
         */
        set alternativeJumpToStepCheck(jumpCheckImplementation) {
            _jumpToStepCheck = jumpCheckImplementation;
        },

        /**
         * Reset the current step and step history of the flow
         *
         * @param {Object} currentStep - New current step in flow
         * @param {Array} steps        - New step history, array of step names or step objects
         * @param {Object} snapShot    - New snapshot
         *
         * @throws Error if currentStep does not exist as a step in the flow
         * @throws Error if any of the steps elements do not exist as steps in the flow
         *
         */
        resetFlowProgress: function resetFlowProgress(currentStep, steps, snapShot) {
            steps = steps.map(function (step) {
                return FlowUtil.getStepObject(step, flow);
            });
            stepHistory.length = 0;
            stepHistory = stepHistory.concat(steps);
            flow.currentStep = FlowUtil.getStepObject(currentStep, flow);
            this.snapshot = snapShot;
        },


        /**
         * Check to see if it is possible to transition to this step from the current step
         *
         * @param {Object} targetStep - Step to transition to.
         * @param {Object} [model] - Model to use for transition check function
         *
         * @returns {Boolean}
         */
        isStepAccessibleFromCurrent: function isStepAccessibleFromCurrent(targetStep, model) {
            return _jumpToStepCheck(this, this.currentStep, targetStep, model);
        },


        /**
         * A deep clone of the model is stored on every next transition
         *
         * @returns {Object} - the latest clone of the model
         */
        get snapshot() {
            return currentSnapshot;
        },

        /**
         * Take a clone of the model parameter to store as the latest snapshot
         *
         * @param {Object} model - object to be cloned
         */
        set snapshot(model) {
            currentSnapshot = snapshotBuilder(model);
        },

        /**
         * The name of the current step in the flow
         *
         * @returns {Object} The current step object in the flow
         */
        get currentStep() {
            return flow.currentStep || flow.entry;
        },

        /**
         * Ordered list of steps based on the history of steps we have passed through and the
         * future steps that can be reached based on the model snapshot.
         * (Future steps will be included until an action transition is reached or a branch function returns undefined)
         *
         * @returns {Array}
         */
        get steps() {
            // add the steps that we have passed through already
            var steps = stepHistory.concat();
            if (steps.length === 0) {
                // Getting steps when nothing is in the history and entry is a junction
                return [];
            }

            var nextStep = void 0;

            // add forward steps based on the model snapshot
            do {
                nextStep = FlowUtil.getNextStep(flow, steps[steps.length - 1], this.snapshot);
                if (nextStep) {
                    var previouslySeenIndex = getStepIndexStepArray(nextStep.name, steps);
                    if (previouslySeenIndex !== -1) {
                        // return the steps up until the cycle is encountered
                        return steps.slice(0, previouslySeenIndex + 1);
                    }
                    steps.push(nextStep);
                }
            } while (nextStep);

            return steps;
        },

        /**
         * Ordered list of steps based on the history of steps we have passed through. Does not include future steps
         *
         * @returns {Array}
         */
        get visitedSteps() {
            return stepHistory.concat();
        },

        /**
         * Progress to the next step in the flow based on the model parameter. If the current step is invalid, remain on this step.
         *
         * @param {Object} model - object passed to transition functions
         * @param {Object} [extraArgs]   - Map of arguments passed to branch functions and step listeners
         *
         * @returns {Promise} - promise that resolves to next step
         */
        next: function next(model, extraArgs) {
            if (this.currentStep.isValid && !this.currentStep.isValid()(model, extraArgs)) {
                //console.log('Remaining on current step, as it is invalid.');
                // current step is invalid, do not proceed to next step
                return Q.resolve({
                    model: model,
                    step: this.currentStep,
                    extraArgs: extraArgs
                });
            }

            var self = this;
            var prevStep = this.currentStep;
            return goToStepForEvent(this, 'onNext', model, this.currentStep, extraArgs).then(function (result) {
                publishWizardEvent('next', result.model, prevStep, self.currentStep);
                return result;
            });
        },


        /**
         * Transition to a step in the flow in response to a named event
         * Similar in behavior to onNext behavior where we first check that the model is valid.
         *
         * @param {String} eventName - name of event
         * @param {Object} model - object passed to transition functions
         * @param {Object} [extraArgs]   - Map of arguments passed to branch functions and step listeners
         *
         * @returns {Promise} - promise that resolves to next step
         */
        event: function event(eventName, model) {
            var extraArgs = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

            if (this.currentStep.isValid && !this.currentStep.isValid()(model, extraArgs)) {
                // current step is invalid, do not proceed to next step
                return Q.resolve({
                    model: model,
                    step: this.currentStep,
                    extraArgs: extraArgs
                });
            }

            var self = this;
            var previousStep = this.currentStep;
            return goToStepForEvent(this, eventName, model, this.currentStep, extraArgs).then(function (result) {
                publishWizardEvent('event', model, previousStep, self.currentStep);
                return result;
            });
        },


        /**
         * Move back to a previous step in the flow. If there is a onPrevious handler function
         * defined then that function will be used to determine the previous step. If no function
         * has been entered then the default implementation will return the previous
         * step in the list of ordered steps.
         *
         * @param {*} [_model] - if a model is not provided then the snapshot will be used
         * @param {Object} [_extraArgs]   - Map of arguments passed to branch functions and step listeners
         * @returns {Promise} - promise that resolves to the previous step
         *
         */
        previous: function previous(_model, _extraArgs) {
            var _this = this;

            var prevStep = void 0;
            var prevStepIndex = void 0;
            var fromStep = this.currentStep;
            var hasPreviousHandler = this.currentStep.onPrevious();

            function transitionToPrevious(transitionModel, currentStep) {
                if (!hasPreviousHandler) {
                    // no onPrevious handler defined in flow definition so provide default behavior
                    var currentStepIndex = _.findIndex(stepHistory, function (step) {
                        return step.name === currentStep.name;
                    });
                    prevStepIndex = currentStepIndex - 1;
                    if (prevStepIndex < 0) {
                        throw new Error('Could not find previous step from current step', currentStep);
                    }
                    prevStep = stepHistory[prevStepIndex];
                    flow.currentStep = prevStep;
                } else {
                    // custom handler for previous, may return undefined to act as a gate point in the flow
                    prevStep = hasPreviousHandler(transitionModel, _extraArgs);
                    if (!prevStep) {
                        prevStepIndex = _.findIndex(stepHistory, function (step) {
                            return step.name === currentStep.name;
                        });
                    } else {
                        prevStep = FlowUtil.getStepObject(prevStep, flow);
                        flow.currentStep = prevStep;
                        prevStepIndex = _.findIndex(stepHistory, function (step) {
                            return step.name === prevStep.name;
                        });
                    }
                }
                stepHistory = stepHistory.slice(0, prevStepIndex + 1);
                if (fromStep.name !== flow.currentStep.name) {
                    executeStepChangeListeners(transitionModel, fromStep, flow.currentStep, _extraArgs);
                    publishWizardEvent('previous', transitionModel, fromStep, flow.currentStep);
                }
            }

            if (_model) {
                // check that model is valid, if not then we need to give the user a warning that they might lose information
                return modalBehavior.snapshotResetConfirmation(_model)
                // model is valid so proceed without resetting model to snapshot
                .then(function (model) {
                    return transitionToPrevious(model, _this.currentStep);
                }).catch(function (error) {
                    if (error === 'cancelled') {
                        // navigation cancelled
                        return false;
                    }
                    // confirmed reset to snapshot and proceed with navigation
                    transitionToPrevious(_this.snapshot, _this.currentStep);
                });
            }
            var deferred = Q.defer();
            deferred.promise.then(function () {
                return transitionToPrevious(_this.snapshot, _this.currentStep);
            });
            deferred.resolve();
            return deferred.promise;
        },


        /**
         * Move directly to a step or junction. Step must be in the list of flow steps.
         * A check is performed to determine if the target step is accessible
         *
         * @param {String|Object} step - target step
         * @param {Object} [_model] - object passed to transition functions, if undefined then the snapshot is used.
         * @param {Object} [_extraArgs]   - Map of arguments passed to branch functions and step listeners
         * @param {Boolean} [force] - perform transition bypassing any checks and execute step listener functions
         *                              even in the case where the target step is the same as the current step.
         *                              Defaults to false.
         *
         * @throws {Error} if step does not reference a step in the list of ordered steps
         * @throws {Error} if transition to step is not allowed
         *
         * @returns {Promise} Returned promise is resolved when cancel confirmed or rejected if cancel is dismissed
         *
         */
        jumpToStep: function jumpToStep(step, _model, _extraArgs, force) {
            var _this2 = this;

            var targetStep = FlowUtil.getStepObject(step, flow);
            if (!targetStep) {
                throw new Error('Target step does not exist in the flow', targetStep);
            }
            var fromStep = this.currentStep;

            function transitionToStep(modelOrSnapshot) {
                var targetStepIndex = _.findIndex(stepHistory, function (aStep) {
                    return aStep.name === targetStep.name;
                });
                stepHistory = stepHistory.slice(0, targetStepIndex + 1);
                flow.currentStep = targetStep;
                executeStepChangeListeners(modelOrSnapshot, fromStep, targetStep, _extraArgs);
                publishWizardEvent('jumpToStep', modelOrSnapshot, fromStep, flow.currentStep);
            }

            function checkForIntermediateGateFns(modelOrSnapshot) {
                var currentStepIndex = _.findIndex(stepHistory, function (aStep) {
                    return aStep.name === fromStep.name;
                });
                var targetStepIndex = _.findIndex(stepHistory, function (aStep) {
                    return aStep.name === targetStep.name;
                });
                if (targetStepIndex < currentStepIndex) {
                    var intermediateSteps = stepHistory.slice(targetStepIndex, currentStepIndex + 1);
                    // return true if there are any gate steps that evaluate to falsey between the current step and the target
                    return intermediateSteps.some(function (interStep) {
                        var hasPreviousHandler = interStep.onPrevious();
                        return hasPreviousHandler ? !hasPreviousHandler(modelOrSnapshot) : false;
                    });
                }
                return false;
            }

            _model = _model || this.snapshot;

            if (force !== true && (!_jumpToStepCheck(this, this.currentStep, targetStep, _model) || checkForIntermediateGateFns(_model))) {
                // transition not allowed
                return false;
            }

            if (targetStep.name === this.currentStep.name && !force) {
                // nothing to do
                return;
            } else if (!modalBehavior.snapshotResetConfirmation || targetStep.name === this.currentStep.name && force) {
                transitionToStep(_model);
                return;
            }

            return modalBehavior.snapshotResetConfirmation(_model)
            // model is valid so proceed without resetting to snapshot
            .then(function (model) {
                return transitionToStep(model);
            }).catch(function (error) {
                if (error === 'cancelled') {
                    // navigation cancelled
                    return false;
                }
                // confirmed reset to snapshot and proceed with navigation
                transitionToStep(_this2.snapshot);
            });
        },


        /**
         * Transition to exit step or junction, calling event listeners
         * A check is performed to determine if the target is an exit point (has not onNext handler)
         *
         * @param {String|Object} exitStepOrJunction - target
         * @param {Object} [_model] - object passed to transition functions, if undefined then the snapshot is used.
         * @param {Object} [_extraArgs]   - Map of arguments passed to branch functions and step listeners
         *
         * @throws {Error} if stepOrJunction does not reference a step or junction in the flow exit point
         * @throws {Error} if transition to step or junction step is not allowed
         *
         */
        jumpToExit: function jumpToExit(exitStepOrJunction, _model) {
            var _extraArgs = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

            exitStepOrJunction = FlowUtil.getStepOrJunctionObject(exitStepOrJunction, flow);

            if (!exitStepOrJunction) {
                throw new Error('Target step does not exist in the flow', exitStepOrJunction);
            }
            if (!FlowUtil.isExitPoint(exitStepOrJunction, flow)) {
                throw new Error('Target step does is not an exit point, contains a onNext handler', exitStepOrJunction);
            }
            flow.currentStep = exitStepOrJunction;
            executeStepChangeListeners(_model, exitStepOrJunction, exitStepOrJunction, _extraArgs);
        },


        /**
         * Prompts the user to cancel the flow. Assumes that the flow has a 'cancel' junction or step.
         *
         * @param {Object} [_model] - object passed to transition functions, if undefined then the snapshot is used.
         * @param {Object} [_extraArgs]   - Map of alternative cancel messages title: message: description:
         *
         * @throws {Error} if flow has no cancel step or junction
         *
         * @returns {Promise}
         *
         */
        cancel: function cancel(_model) {
            var _extraArgs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

            var cancelPoint = FlowUtil.getStepOrJunctionObject('cancel', flow);
            if (!cancelPoint) {
                throw new Error('Flow requires a cancel exit junction to be defined');
            }
            if (typeof cancelPoint.data().confirmationMsg === 'function') {
                _extraArgs.message = cancelPoint.data().confirmationMsg(_model);
            }
            if (typeof cancelPoint.data().confirmationModalArgs === 'function') {
                Object.assign(_extraArgs, cancelPoint.data().confirmationModalArgs());
            }
            var previousStep = flow.currentStep;

            //console.log('test');
            return Q.resolve();

            // return modalBehavior.cancelConfirmation(_model, _extraArgs)
            //         .then(snapshotOrModel => {
            //             const transactionPromise = goToStepForEvent(this, 'onNext', snapshotOrModel, cancelPoint, _extraArgs);
            //             publishWizardEvent('cancel', _model, previousStep);
            //             return transactionPromise;
            //         })
            //         .catch(() => {
            //             // cancel transition declined so don't perform transition
            //             return Q.reject();
            //         });
        }
    };
}


//////////////////
// WEBPACK FOOTER
// ./app/customer/js/src/edge/platform/flow/FlowModel.js
// module id = 750
// module chunks = 0